import type { SiteCmsDomainResponse, SiteCmsPublicationState } from '@shared/types/entities';

export type CmsPublishBlocker = {
  id: string;
  title: string;
  detail: string;
  action: 'save' | 'pages' | 'documents' | 'retry' | 'operator' | 'finish';
};

type CmsPublishGateInput = {
  state?: SiteCmsPublicationState;
  domain?: SiteCmsDomainResponse;
  loading: boolean;
  unavailable: boolean;
  requiresSave: boolean;
  revision: number;
  alreadyOnline?: boolean;
  pendingForms: boolean;
  libraryBusy: boolean;
  busy: boolean;
  accessSuspended?: boolean;
  editorReloading?: boolean;
  issueCount: number;
  missingImageCount: number;
  missingDocumentCount: number;
  missingCompetitionCount: number;
};

/**
 * One explainable list for *all* reasons that the publish action is disabled.
 * A local preview is deliberately not treated as public DNS/TLS approval.
 * No server-side checks are bypassed by the UI.
 */
export function cmsPublishBlockers(input: CmsPublishGateInput): CmsPublishBlocker[] {
  const blockers: CmsPublishBlocker[] = [];
  const add = (id: string, title: string, detail: string, action: CmsPublishBlocker['action']) =>
    blockers.push({ id, title, detail, action });

  if (input.loading) {
    add(
      'loading',
      'Vérification en cours',
      'Les droits, le domaine et les ressources sont en cours de chargement.',
      'finish',
    );
    return blockers;
  }
  if (input.unavailable || !input.state) {
    add(
      'unavailable',
      'Statut impossible à vérifier',
      'Les informations de publication ne sont pas disponibles. Réessayez sans perdre le brouillon.',
      'retry',
    );
    return blockers;
  }
  if (input.accessSuspended) {
    add(
      'access',
      'Accès temporairement suspendu',
      'La connexion ou les droits de cette structure ne peuvent pas être vérifiés. Cliquez sur « Réessayer la vérification des droits » en haut du CMS.',
      'finish',
    );
  }
  if (input.editorReloading) {
    add(
      'reloading',
      'Rechargement du brouillon',
      'Attendez la fin du rechargement avant de confirmer cette version.',
      'finish',
    );
  }
  if (input.busy) {
    add('busy', 'Opération de publication en cours', 'Attendez son résultat avant d’en envoyer une autre.', 'finish');
  }
  if (input.requiresSave || input.revision === 0) {
    add('save', 'Brouillon à enregistrer', 'Enregistrez ou actualisez la révision avant de la diffuser.', 'save');
  }
  if (input.alreadyOnline && !input.requiresSave) {
    add(
      'already-online',
      'Cette révision est déjà en ligne',
      'Aucune publication n’est nécessaire. Modifiez et enregistrez le brouillon pour préparer une nouvelle révision.',
      'finish',
    );
  }
  if (input.pendingForms) {
    add('forms', 'Formulaire inachevé', 'Terminez ou annulez les modifications de formulaire en cours.', 'finish');
  }
  if (input.libraryBusy) {
    add('library', 'Import ou édition de média en cours', 'Terminez cette opération dans la bibliothèque.', 'finish');
  }
  if (input.issueCount > 0) {
    add(
      'content',
      `${input.issueCount} erreur(s) dans le contenu`,
      'Corrigez les champs et les destinations signalés dans l’éditeur.',
      'pages',
    );
  }
  if (input.missingImageCount || input.missingDocumentCount) {
    add(
      'media',
      'Image ou document indisponible',
      `${input.missingImageCount} image(s) et ${input.missingDocumentCount} PDF manquant(s) dans les contenus publics. Remplacez-les ou retirez leurs références.`,
      'documents',
    );
  }
  if (input.missingCompetitionCount) {
    add(
      'competitions',
      'Concours non disponible',
      `${input.missingCompetitionCount} concours sélectionné(s) ne sont plus disponibles. Corrigez leur section dans Pages.`,
      'pages',
    );
  }
  if (input.state.readiness?.length) {
    for (const check of input.state.readiness) {
      if (check.status === 'blocked') {
        add(check.id, check.label, check.detail, check.managedBy === 'boulesconnect' ? 'operator' : 'finish');
      }
    }
  }
  if (
    !input.state.canPublish &&
    !blockers.some(blocker => blocker.action === 'operator' || blocker.id === 'owner_public')
  ) {
    // Compatibility with older API payloads, incomplete server diagnostics,
    // or a capability changed since the page initially loaded.
    const domainExplanation = !input.domain?.hostname
      ? 'Aucun domaine n’est encore attribué : BoulesConnect doit le réserver.'
      : !input.domain.verifiedAt
        ? 'La preuve DNS de ce domaine doit être vérifiée par BoulesConnect.'
        : 'La publication doit être autorisée par BoulesConnect.';
    add('server', 'Autorisation de mise en ligne absente', input.state.reason ?? domainExplanation, 'operator');
  }
  return blockers;
}
